import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import NotAuthorized from './NotAuthorized';

export default function ProtectedRoute({ children, permission, role, fallback = '/auth' }) {
  const { isAuthenticated, isLoading, hasPermission, hasRole, user, permissions } = useAuth();
  const location = useLocation();

  // Enhanced debugging
  console.log('🔍 ProtectedRoute DEBUG:', {
    path: location.pathname,
    isAuthenticated,
    isLoading,
    user: user?.email,
    userRole: user?.role,
    userPermissions: permissions,
    requiredPermission: permission,
    requiredRole: role,
    permissionCheck: permission ? hasPermission(permission) : 'N/A',
    roleCheck: role ? hasRole(role) : 'N/A'
  });

  // Show loading spinner while checking authentication
  if (isLoading) {
    console.log('🔄 ProtectedRoute: Still loading authentication state');
    return (
      <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#EDF0F1'}}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{borderColor: '#00C5CB'}}></div>
          <p className="text-gray-600 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to auth if not authenticated
  if (!isAuthenticated) {
    console.log('🚨 ProtectedRoute: User not authenticated, redirecting to auth');
    return <Navigate to={fallback} state={{ from: location }} replace />;
  }

  // Check permission if required
  if (permission && !hasPermission(permission)) {
    console.log(`🚨 ProtectedRoute: Missing permission "${permission}"`);
    console.log(`🔍 Available permissions:`, permissions);
    console.log(`🔍 User role:`, user?.role);
    return <NotAuthorized requiredPermission={permission} />;
  }

  // Check role if required
  if (role && !hasRole(role)) {
    console.log(`🚨 ProtectedRoute: Missing role "${role}"`);
    return <NotAuthorized requiredRole={role} />;
  }

  console.log('✅ ProtectedRoute: Access granted');
  // Render children if all checks pass
  return children;
}